import {
  ChangeDetectorRef,
  Directive,
  EventEmitter,
  Input,
  OnDestroy,
  Optional,
  Output,
  Self,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { asViewRef, coalesceChangeDetection } from '@recruitee/utils';

@Directive({ standalone: true })
export abstract class RtInput<V> implements ControlValueAccessor, OnDestroy {
  public get disabled(): boolean {
    return this._disabled;
  }

  @Input()
  public set disabled(value: boolean) {
    this._disabled = value;
  }

  @Input() public value: V | null = null;
  @Input() public readOnly: boolean = false;

  @Output() public valueChange: EventEmitter<V> = new EventEmitter();
  @Output() public focus: EventEmitter<UIEvent> = new EventEmitter();
  @Output() public blur: EventEmitter<UIEvent> = new EventEmitter();

  protected ngOnDestroy$ = new Subject<void>();

  private _disabled: boolean = false;
  private scheduleDetectChanges = coalesceChangeDetection(() => {
    if (asViewRef(this.cdRef).destroyed) return;

    this.cdRef.detectChanges();
  });

  constructor(
    protected cdRef: ChangeDetectorRef,
    @Optional() @Self() protected ngControl?: NgControl,
  ) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public writeValue(value: V | undefined): void {
    if (value !== undefined) {
      this.setValue(value);
      // TODO remove this.
      // This is left for backwards compability with components using this component with formControl
      // but not subscribing itself to its changes, while they should.
      // (parent component should listen to changes of its' form controls and update itself when needed,
      //  not the opposite: child shouldn't decide about when a parent should be updated)
      // removal in progress
      this.scheduleDetectChanges();
    }
  }

  public registerOnChange(fn: (value: V) => void): void {
    this.valueChange.pipe(takeUntil(this.ngOnDestroy$)).subscribe(fn);
  }

  public registerOnTouched(fn: () => void): void {
    this.blur.pipe(takeUntil(this.ngOnDestroy$)).subscribe(fn);
  }

  public setDisabledState(disabled: boolean): void {
    this._disabled = disabled;
    this.scheduleDetectChanges();
  }

  public handleChange(value: V): void {
    this.valueChange.emit(value);

    // Mutate the value only when controlled by a FormControl
    if (this.ngControl) {
      this.setValue(value);
    }
  }

  protected setValue(value: V): void {
    this.value = value;
    if (this.ngControl) {
      this.scheduleDetectChanges();
    }
  }
}

@Directive({ standalone: true })
export abstract class RtTextInput extends RtInput<string> {
  public abstract getNativeInputElement(): HTMLInputElement | HTMLTextAreaElement;

  public insertAtCaret(
    value: string | { toString: () => string },
    type: 'plaintext' | 'html' | 'placeholder' | 'link',
  ): void;
  public insertAtCaret(value: string): void {
    const str = value.toString();
    const element = this.getNativeInputElement();
    const caretPosition = element.selectionStart!;
    const caretPositionAfterChange = caretPosition + str.length;
    const templateText = element.value;

    this.handleChange(
      `${templateText.substring(0, caretPosition)}${str}${templateText.substring(caretPosition)}`,
    );

    element.focus();
    element.setSelectionRange(caretPositionAfterChange, caretPositionAfterChange);
  }
}
